import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, LogOut, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoginCard } from '@/components/myPay/LoginCard';
import { PinCard } from '@/components/myPay/PinCard';
import { MonthView } from '@/components/myPay/MonthView';
import { LangToggle } from '@/components/myPay/LangToggle';
import { useIdle } from '@/components/myPay/useIdle';
import { makeT } from '@/components/myPay/i18n';
import { currentMonth, myPayApi, myPayStore } from '@/services/myPayApi';

const KIOSK_IDLE_MS = 120000;
const IDLE_MS = 30 * 60000;
const LOGIN_ERR = { 401: 'wrongLogin', 429: 'tooMany', 400: 'noDob', 423: 'pinLocked' };
const errKey = (e, map = {}) => (e?.response ? map[e.response.status] || 'loadFailed' : 'noNetwork');
const loginErr = (e, withPin) => e?.response?.data?.detail?.code || (withPin && e?.response?.status === 401 ? 'wrongPin' : errKey(e, LOGIN_ERR));

/** Public "My Attendance & Pay" — staff sign in with Staff ID + date of birth (no WMS account). */
export default function MyPayPortal() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromKiosk = params.get('from') === 'kiosk';
  const [lang, setLangS] = useState(myPayStore.lang());
  const t = useMemo(() => makeT(lang), [lang]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(params.get('site') || myPayStore.site());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booting, setBooting] = useState(!!myPayStore.token());
  const [errorKey, setErrorKey] = useState('');
  const [pinStep, setPinStep] = useState(null);
  const [back, setBack] = useState({});

  const setLang = (l) => { myPayStore.setLang(l); setLangS(l); };
  useEffect(() => { myPayStore.setSite(site); }, [site]);
  useEffect(() => { myPayApi.sites().then(setSites).catch(() => {}); }, []);

  const end = useCallback((key = '') => {
    myPayApi.logout(); myPayStore.setToken(null); myPayStore.setMonth(null); setData(null); setPinStep(null); setBack({}); setErrorKey(key);
  }, []);

  const load = useCallback(async (month) => {
    setBusy(true); setErrorKey('');
    try { setData(await myPayApi.month(month)); myPayStore.setMonth(month); } catch (e) {
      if (e?.response?.status === 401) end('sessionEnded'); else setErrorKey(errKey(e));
    } finally { setBusy(false); setBooting(false); }
  }, [end]);

  useEffect(() => { if (myPayStore.token()) load(myPayStore.month() || currentMonth()); }, [load]);

  const login = async (sid, dob, extra = {}) => {
    setBusy(true); setErrorKey('');
    try {
      const r = await myPayApi.login(sid, dob, extra);
      if (r.step) { setPinStep({ mode: r.step, sid, dob, name: r.staff_name }); setBusy(false); return; }
      setPinStep(null); myPayStore.setToken(r.token); await load(r.months[0]);
    } catch (e) { setErrorKey(loginErr(e, !!extra.pin)); setBusy(false); }
  };
  const submitPin = (pin) => login(pinStep.sid, pinStep.dob, pinStep.mode === 'set_pin' ? { new_pin: pin } : { pin });

  useIdle(!!data, fromKiosk ? KIOSK_IDLE_MS : IDLE_MS, () => { end('sessionEnded'); if (fromKiosk) navigate('/attendance/kiosk'); });

  return (
    <div className="min-h-screen bg-[#FFFBF5] mypay-grain" data-testid="my-pay-portal">
      <header className="bg-[#1A1F2E] text-white print:hidden">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-3 px-4">
          {fromKiosk && <Button variant="ghost" size="icon" onClick={() => { end(); navigate('/attendance/kiosk'); }} className="text-white hover:bg-white/10" data-testid="mypay-back-kiosk"><ArrowLeft className="h-5 w-5" /></Button>}
          <Wallet className="h-6 w-6 text-amber-400" />
          <h1 className="min-w-0 truncate text-base font-bold sm:text-xl" data-testid="mypay-title">{t('title')}</h1>
          <div className="ml-auto flex items-center gap-2">
            <LangToggle lang={lang} setLang={setLang} dark />
            {data && <Button variant="outline" size="sm" onClick={() => end()} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="mypay-logout-btn"><LogOut className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">{t('logout')}</span></Button>}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
        {booting && <Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-600" />}
        {!booting && !data && (
          <div className="space-y-8">
            <div className="mypay-rise text-center sm:text-left sm:max-w-md sm:mx-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2D5F4F]">{t('selfService')}</p>
              <h2 className="mt-2 text-3xl font-bold text-[#1A1F2E] sm:text-4xl">{t('title')}</h2>
            </div>
            {pinStep
              ? <PinCard key={pinStep.mode} t={t} mode={pinStep.mode} name={pinStep.name} onSubmit={submitPin} onEdit={() => setErrorKey('')}
                  onBack={() => { setBack({ sid: pinStep.sid, dob: pinStep.dob }); setPinStep(null); setErrorKey(''); }} busy={busy} error={errorKey && t(errorKey)} />
              : <LoginCard t={t} sites={sites} site={site} setSite={setSite} onLogin={login} busy={busy} error={errorKey && t(errorKey)} initial={back} />}
          </div>
        )}
        {data && (
          <div className="mypay-rise space-y-3">
            {errorKey && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mypay-error">{t(errorKey)}</p>}
            <MonthView data={data} t={t} lang={lang} onMonth={load} busy={busy} />
          </div>
        )}
      </main>
    </div>
  );
}
